import { z } from "zod";
/**
 * Room file sharing. Files are uploaded in fixed-size parts (except the last)
 * through the API server and stored privately; members of the room list and
 * download them. Encryption is optional and happens in the browser: the server
 * only ever sees ciphertext plus the public parameters below.
 */
/** Stored bytes per upload part; only the last part may be shorter. */
export const ROOM_FILE_PART_BYTES = 8 * 1024 * 1024;
/** AES-GCM authentication tag appended to every encrypted chunk. */
export const ROOM_FILE_TAG_BYTES = 16;
/** Plaintext bytes per encrypted chunk, so one ciphertext chunk fills one part. */
export const ROOM_FILE_CHUNK_BYTES = ROOM_FILE_PART_BYTES - ROOM_FILE_TAG_BYTES;
export const ROOM_FILE_NAME_MAX_LENGTH = 255;
/** How long a file is kept. `room` keeps it until the room itself is deleted. */
export const RoomFileRetentionSchema = z.enum(["1h", "1d", "1w", "room"]);
export const ROOM_FILE_RETENTION_MS = {
    "1h": 60 * 60 * 1000,
    "1d": 24 * 60 * 60 * 1000,
    "1w": 7 * 24 * 60 * 60 * 1000,
};
const base64 = (max) => z.string().min(1).max(max).regex(/^[A-Za-z0-9+/]+={0,2}$/);
/**
 * Public parameters of a client-side encrypted file (version 1):
 * PBKDF2-SHA256 derives an AES-256-GCM key from the password and `salt`.
 * The plaintext is cut into `ROOM_FILE_CHUNK_BYTES` chunks; chunk `i` uses the
 * IV `noncePrefix ‖ u32be(i)` and binds its index and a final flag as
 * additional data. `keyCheck` is the tag of an empty plaintext under a
 * reserved IV, so a wrong password is detected before downloading.
 * `keySource: "room"` means the room password was used.
 */
export const RoomFileEncryptionSchema = z.object({
    v: z.literal(1),
    keySource: z.enum(["room", "custom"]),
    kdf: z.literal("PBKDF2-SHA256"),
    iterations: z.number().int().min(100_000).max(10_000_000),
    salt: base64(64),
    noncePrefix: base64(16),
    keyCheck: base64(64),
});
/** File names are shown and offered as download names; no paths or control characters. */
export const RoomFileNameSchema = z.string().trim().min(1).max(ROOM_FILE_NAME_MAX_LENGTH).refine((name) => !/[\\/\u0000-\u001f\u007f]/.test(name), {
    message: "File name must not contain path separators or control characters",
});
export const RoomFileSchema = z.object({
    id: z.string(),
    roomId: z.string(),
    name: z.string(),
    mime: z.string(),
    /** Original (plaintext) size in bytes. */
    size: z.number().int().nonnegative(),
    /** Bytes held in storage and counted against the instance quota. */
    storedSize: z.number().int().nonnegative(),
    uploadedBy: z.string(),
    uploaderName: z.string(),
    uploadedAt: z.number(),
    retention: RoomFileRetentionSchema,
    /** Epoch ms after which the file is gone; null while its room exists. */
    expiresAt: z.number().nullable(),
    encryption: RoomFileEncryptionSchema.optional(),
});
export const RoomFileUsageSchema = z.object({
    usedBytes: z.number().int().nonnegative(),
    maxFileBytes: z.number().int().nonnegative(),
    maxTotalBytes: z.number().int().nonnegative(),
});
export const RoomFileErrorCodeSchema = z.enum([
    "FILE_SHARING_DISABLED",
    "FILE_TOO_LARGE",
    "STORAGE_QUOTA_EXCEEDED",
    "TOO_MANY_UPLOADS",
    "NOT_IN_ROOM",
    "ROOM_NOT_FOUND",
    "FILE_NOT_FOUND",
    "UPLOAD_NOT_FOUND",
    "UPLOAD_INCOMPLETE",
    "INVALID_PART",
]);
export const ListRoomFilesQuerySchema = z.object({
    roomId: z.string().min(1).max(256),
});
export const ListRoomFilesResponseSchema = z.object({
    files: z.array(RoomFileSchema),
    usage: RoomFileUsageSchema,
});
export const BeginRoomFileUploadRequestSchema = z.object({
    roomId: z.string().min(1).max(256),
    name: RoomFileNameSchema,
    mime: z.string().max(255).default(""),
    size: z.number().int().positive(),
    retention: RoomFileRetentionSchema,
    uploaderName: z.string().trim().max(256).default(""),
    encryption: RoomFileEncryptionSchema.optional(),
});
export const BeginRoomFileUploadResponseSchema = z.object({
    uploadId: z.string(),
    fileId: z.string(),
    storedSize: z.number().int().positive(),
    partBytes: z.number().int().positive(),
    partCount: z.number().int().positive(),
});
export const RoomFileUploadParamsSchema = z.object({
    uploadId: z.string().min(1).max(64),
});
export const RoomFileUploadPartParamsSchema = RoomFileUploadParamsSchema.extend({
    partNumber: z.coerce.number().int().min(1).max(10_000),
});
export const UploadRoomFilePartResponseSchema = z.object({
    partNumber: z.number().int().positive(),
    size: z.number().int().nonnegative(),
});
export const CompleteRoomFileUploadResponseSchema = z.object({
    file: RoomFileSchema,
    usage: RoomFileUsageSchema,
});
export const AbortRoomFileUploadResponseSchema = z.object({
    success: z.literal(true),
    uploadId: z.string(),
});
export const DeleteRoomFileRequestSchema = z.object({
    roomId: z.string().min(1).max(256),
    fileId: z.string().min(1).max(64),
});
export const DeleteRoomFileResponseSchema = z.object({
    success: z.literal(true),
    fileId: z.string(),
});
export const DownloadRoomFileParamsSchema = z.object({
    fileId: z.string().min(1).max(64),
});
export const DownloadRoomFileQuerySchema = z.object({
    roomId: z.string().min(1).max(256),
});
/** Bytes held in storage for a file of `size` plaintext bytes. */
export function roomFileStoredSize(size, encrypted) {
    if (!encrypted)
        return size;
    const chunks = Math.max(1, Math.ceil(size / ROOM_FILE_CHUNK_BYTES));
    return size + chunks * ROOM_FILE_TAG_BYTES;
}
/** Number of upload parts for `storedSize` stored bytes. */
export function roomFilePartCount(storedSize) {
    return Math.max(1, Math.ceil(storedSize / ROOM_FILE_PART_BYTES));
}
/** Expected byte length of part `partNumber` (1-based). */
export function roomFilePartSize(storedSize, partNumber) {
    const count = roomFilePartCount(storedSize);
    if (partNumber < 1 || partNumber > count)
        return -1;
    return partNumber < count
        ? ROOM_FILE_PART_BYTES
        : storedSize - (count - 1) * ROOM_FILE_PART_BYTES;
}
/** Room file events: delivered only to members of the room. */
export const RoomFileAddedEventSchema = z.object({
    roomId: z.string(),
    file: RoomFileSchema,
    usedBytes: z.number().int().nonnegative(),
});
export const RoomFilesRemovedEventSchema = z.object({
    roomId: z.string(),
    fileIds: z.array(z.string()),
    reason: z.enum(["deleted", "expired", "room-deleted"]),
    usedBytes: z.number().int().nonnegative().optional(),
});
